import { useEffect, useState } from 'react'

/**
 * Global "an agent is working" lock, shared across every agent interaction (chat, improve-with-AI,
 * SVG generation): while at least one task is in flight, every OTHER agent trigger disables itself
 * — only one local-agent run at a time. A counter supports overlapping registrations. (This used to
 * also drive a single global "Niobe is working…" toast; that overlay was removed as redundant with
 * each surface's own busy state, e.g. ChatPanel's in-panel indicator — the lock itself stays.)
 */
interface BusyState {
  busy: boolean
}

let count = 0
const listeners = new Set<(s: BusyState) => void>()

function snapshot(): BusyState {
  return { busy: count > 0 }
}
function emit(): void {
  const s = snapshot()
  for (const l of listeners) l(s)
}

/** Mark an agent task as started; returns a release function (call it once the task finishes). */
export function beginAgentTask(): () => void {
  count += 1
  emit()
  let released = false
  return () => {
    if (released) return
    released = true
    count = Math.max(0, count - 1)
    emit()
  }
}

/** Imperative check (for guards outside React render, e.g. a restore handler). */
export function isAgentBusy(): boolean {
  return count > 0
}

/** Reactive view of the global agent-busy state. */
export function useAgentBusy(): BusyState {
  const [state, setState] = useState<BusyState>(snapshot)
  useEffect(() => {
    const l = (s: BusyState): void => setState(s)
    listeners.add(l)
    l(snapshot())
    return () => {
      listeners.delete(l)
    }
  }, [])
  return state
}
